import React from 'react';
import { Sparkles, RotateCcw, Eye, EyeOff, ChevronDown, ChevronRight, Save, Trash2, Bookmark } from 'lucide-react';

/**
 * LetterheadLayoutEditor — layer-based editor for the programmatic
 * letterhead variant. Each of the ~6 shipped layers (koru, watermark,
 * RTR shield, wordmark, office phone, contact footer) is exposed as a
 * collapsible card with numeric inputs for x/y/width/height/opacity
 * plus a visibility toggle.
 *
 * Only visible / editable when the active `letterhead_variant` is
 * `programmatic_v1` — otherwise renders a hint prompting the user to
 * switch variants first (so tweaks aren't invisible).
 *
 * All coordinates are millimetres, origin at page BOTTOM-LEFT
 * (ReportLab convention). A4 = 210 × 297 mm.
 *
 * Persistence is via `PUT /report-settings/letterhead-layout` — the
 * backend merges partial saves against the shipped defaults so a
 * missing field never wipes a layer.
 */
export default function LetterheadLayoutEditor({ api }) {
  const [loading, setLoading]   = React.useState(true);
  const [layout, setLayout]     = React.useState(null);
  const [error, setError]       = React.useState('');
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [pdfBust, setPdfBust]   = React.useState(0);
  const [refAppraisalId, setRefAppraisalId] = React.useState(null);
  const [active, setActive]     = React.useState('');
  const [switching, setSwitching] = React.useState(false);
  const [presets, setPresets]   = React.useState([]);
  const [newPresetName, setNewPresetName] = React.useState('');
  const [savingPreset, setSavingPreset] = React.useState(false);

  const isActive = active === 'programmatic_v1';

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      // Fetch active variant + layout + presets in parallel.
      const [{ data: variants }, { data: layoutDoc }, { data: presetsResp }] = await Promise.all([
        api.get('/report-settings/letterhead-variants'),
        api.get('/report-settings/letterhead-layout'),
        api.get('/report-settings/letterhead-presets'),
      ]);
      setActive(variants.active || '');
      setLayout(layoutDoc);
      setPresets(presetsResp.presets || []);
      if (!refAppraisalId) {
        try {
          const r = await api.get('/appraisals', { params: { limit: 1 } });
          const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
          if (items[0]) setRefAppraisalId(items[0]._id || items[0].id);
        } catch { /* preview stays empty */ }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'failed');
    } finally {
      setLoading(false);
    }
  }, [api, refAppraisalId]);

  React.useEffect(() => { load(); }, [load]);

  // "Switch to programmatic" one-click activator — matches the JPG variants
  // picker UX above; saves the user having to scroll back up.
  const switchToProgrammatic = async () => {
    setSwitching(true);
    try {
      await api.put('/report-settings', { letterhead_variant: 'programmatic_v1' });
      setActive('programmatic_v1');
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Switch failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSwitching(false);
    }
  };

  const patchLayer = async (id, patch) => {
    const nextLayers = layout.layers.map(L => L.id === id ? { ...L, ...patch } : L);
    setLayout({ ...layout, layers: nextLayers });
    try {
      await api.put('/report-settings/letterhead-layout', { ...layout, layers: nextLayers });
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
      load();
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset all letterhead layers to shipped defaults? Custom positions will be wiped.')) return;
    try {
      const { data } = await api.post('/report-settings/letterhead-layout/reset');
      setLayout(data);
      setPdfBust(b => b + 1);
    } catch (e) { alert('Reset failed: ' + e.message); }
  };

  // ── Preset handlers ──────────────────────────────────────────
  const savePresetAs = async () => {
    const name = newPresetName.trim();
    if (!name) { alert('Give the preset a name first.'); return; }
    setSavingPreset(true);
    try {
      await api.post('/report-settings/letterhead-presets', { name });
      setNewPresetName('');
      const { data } = await api.get('/report-settings/letterhead-presets');
      setPresets(data.presets || []);
    } catch (e) {
      alert('Save preset failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingPreset(false);
    }
  };

  const applyPreset = async (preset) => {
    if (!window.confirm(`Load "${preset.name}"? Your current layout will be overwritten (save it first as a new preset if you want to keep it).`)) return;
    try {
      const { data } = await api.post(`/report-settings/letterhead-presets/${preset.id}/apply`);
      setLayout(data);
      setActive('programmatic_v1');   // apply also flips the variant server-side
      setPdfBust(b => b + 1);
    } catch (e) { alert('Apply preset failed: ' + (e?.response?.data?.detail || e.message)); }
  };

  const deletePreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/report-settings/letterhead-presets/${preset.id}`);
      const { data } = await api.get('/report-settings/letterhead-presets');
      setPresets(data.presets || []);
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  if (!isActive) {
    return (
      <section data-testid="letterhead-layout-editor-inactive" className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-800">Letterhead Layout Editor</h4>
        <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
          The layout editor is only available when the active letterhead variant is <span className="font-mono bg-slate-100 px-1 rounded">programmatic_v1</span>.
          Switch below (or in the picker above) to unlock per-layer resizing / repositioning.
        </p>
        <button
          data-testid="switch-to-programmatic-btn"
          onClick={switchToProgrammatic}
          disabled={switching}
          className="mt-2 px-3 py-1.5 text-[11px] font-semibold bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {switching ? 'Switching…' : '→ Switch to Programmatic letterhead'}
        </button>
      </section>
    );
  }

  if (loading) return <div className="p-4 text-xs text-slate-400">Loading layout…</div>;
  if (error)   return <div className="p-4 text-xs text-red-600">Error: {error}</div>;
  if (!layout) return null;

  const previewUrl = refAppraisalId
    ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/detailed-roof-appraisal/${refAppraisalId}?variant=instant_free&inline=1&_bust=${pdfBust}`
    : null;

  return (
    <section data-testid="letterhead-layout-editor" className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Sparkles size={13} className="text-purple-600" />
            Letterhead Layout Editor
          </h4>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
            Resize / reposition each layer independently. All measurements in <b>mm</b> (A4 = 210 × 297 mm, origin at page bottom-left).
            Changes save on blur and the preview refreshes automatically.
          </p>
        </div>
        <button
          data-testid="letterhead-layout-reset-all"
          onClick={resetAll}
          className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:text-rose-600 border border-slate-200 rounded"
        >
          <RotateCcw size={11} /> Reset all
        </button>
      </div>

      {/* Preset bar — save current tweak as a named preset, load / delete existing ones */}
      <div data-testid="preset-bar" className="mb-3 p-2.5 bg-purple-50/60 border border-purple-200 rounded-md">
        <div className="flex items-center gap-1.5 mb-2">
          <Bookmark size={12} className="text-purple-700" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-purple-800">Layout Presets</span>
          <span className="text-[9px] text-purple-700 italic ml-1">
            · save your current tweak and swap between looks (e.g. &ldquo;Print — subtle koru&rdquo;, &ldquo;Digital — bold shield&rdquo;)
          </span>
        </div>

        {/* Save-current row */}
        <div className="flex items-center gap-2 mb-2">
          <input
            data-testid="preset-name-input"
            type="text"
            placeholder="Name for a new preset (e.g. Print — subtle koru)"
            value={newPresetName}
            onChange={e => setNewPresetName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') savePresetAs(); }}
            className="flex-1 px-2 py-1 text-[11px] border border-purple-300 rounded bg-white"
          />
          <button
            data-testid="preset-save-btn"
            onClick={savePresetAs}
            disabled={savingPreset || !newPresetName.trim()}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-40"
          >
            <Save size={11} /> {savingPreset ? 'Saving…' : 'Save current'}
          </button>
        </div>

        {/* Existing presets list */}
        {presets.length === 0 ? (
          <div className="text-[10px] italic text-purple-500">No presets saved yet. Save your current layout to start a library.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {presets.map(p => (
              <div
                key={p.id}
                data-testid={`preset-chip-${p.id}`}
                className="flex items-center gap-1 pl-2 pr-1 py-0.5 bg-white border border-purple-300 rounded-full"
              >
                <button
                  data-testid={`preset-apply-${p.id}`}
                  onClick={() => applyPreset(p)}
                  className="text-[10px] font-semibold text-purple-800 hover:text-purple-900"
                  title={`Load "${p.name}" (${p.layer_count} layers)`}
                >
                  {p.name}
                </button>
                <button
                  data-testid={`preset-delete-${p.id}`}
                  onClick={() => deletePreset(p)}
                  className="text-purple-400 hover:text-rose-600 rounded-full p-0.5"
                  title="Delete preset"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Left col — layer cards */}
        <div className="md:col-span-3 space-y-2" data-testid="letterhead-layer-list">
          {layout.layers.map(L => {
            const isOpen = expanded.has(L.id);
            return (
              <div key={L.id} data-testid={`layer-card-${L.id}`} className={`border rounded-md ${L.visible ? 'border-slate-300' : 'border-slate-200 bg-slate-50 opacity-70'}`}>
                <button
                  onClick={() => toggleExpanded(L.id)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-50"
                >
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex-1">{L.label || L.id}</span>
                  <span className="text-[9px] font-mono text-slate-400">z={L.z_index}</span>
                  <span
                    role="button"
                    data-testid={`layer-visible-toggle-${L.id}`}
                    onClick={(e) => { e.stopPropagation(); patchLayer(L.id, { visible: !L.visible }); }}
                    className={`inline-flex items-center justify-center w-6 h-6 rounded cursor-pointer ${L.visible ? 'text-emerald-700 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}
                    title={L.visible ? 'Hide layer' : 'Show layer'}
                  >
                    {L.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 border-t border-slate-200 grid grid-cols-2 gap-2">
                    <FieldNum label="x (mm)" testid={`layer-${L.id}-x`} value={L.x_mm} onCommit={v => patchLayer(L.id, { x_mm: v })} />
                    <FieldNum label="y (mm)" testid={`layer-${L.id}-y`} value={L.y_mm} onCommit={v => patchLayer(L.id, { y_mm: v })} />
                    <FieldNum label="width (mm)" testid={`layer-${L.id}-w`} value={L.width_mm} onCommit={v => patchLayer(L.id, { width_mm: v })} />
                    <FieldNum label="height (mm)" testid={`layer-${L.id}-h`} value={L.height_mm} onCommit={v => patchLayer(L.id, { height_mm: v })} />
                    <FieldNum label="opacity 0-1" testid={`layer-${L.id}-op`} step="0.05" value={L.opacity} onCommit={v => patchLayer(L.id, { opacity: v })} />
                    <FieldNum label="z-index" testid={`layer-${L.id}-z`} step="1" value={L.z_index} onCommit={v => patchLayer(L.id, { z_index: v })} />
                    {L.kind === 'text_line' && (
                      <div className="col-span-2">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Text</label>
                        <input
                          data-testid={`layer-${L.id}-text`}
                          type="text"
                          defaultValue={L.text || ''}
                          onBlur={e => patchLayer(L.id, { text: e.target.value })}
                          className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded"
                        />
                      </div>
                    )}
                    {L.kind === 'text_block' && (
                      <div className="col-span-2">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Text lines (one per row)</label>
                        <textarea
                          data-testid={`layer-${L.id}-text_lines`}
                          rows={3}
                          defaultValue={(L.text_lines || []).join('\n')}
                          onBlur={e => patchLayer(L.id, { text_lines: e.target.value.split('\n') })}
                          className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded"
                        />
                      </div>
                    )}
                    <div className="col-span-2 text-[9px] text-slate-400 italic mt-1">
                      {L.kind === 'image' ? `image: ${L.asset?.split('/').pop() || '—'}` : `text (${L.font_family} ${L.font_size}pt)`}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right col — live preview */}
        <div className="md:col-span-2" data-testid="letterhead-layout-preview">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Live preview (page 1)</div>
          {previewUrl ? (
            <iframe title="letterhead preview" src={previewUrl} className="w-full border border-slate-300 rounded" style={{ height: 640 }} />
          ) : (
            <div className="text-[11px] text-slate-400 italic">No reference appraisal available</div>
          )}
        </div>
      </div>
    </section>
  );
}

/** Small numeric field with defaultValue + onBlur commit — matches
 * the rest of the audit-studio auto-save UX (no explicit save button). */
function FieldNum({ label, value, onCommit, testid, step = '0.5' }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <input
        data-testid={testid}
        type="number"
        step={step}
        defaultValue={value ?? ''}
        key={`${label}-${value}`}
        onBlur={e => {
          const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
          if (Number.isFinite(v) && v !== value) onCommit(v);
        }}
        className="w-full px-2 py-1 text-[11px] font-mono border border-slate-300 rounded"
      />
    </div>
  );
}
